// libs
import React, { useState, useCallback, useRef, memo } from 'react';

//components
import SortByIcon from '@nykaa/ui-components/Icons/sort';

//styles
import {
  Container,
  SortButton,
  Text,
  SelectedValue,
  Arrow,
  PopUp,
  FilterRadio,
  Wrapper,
} from './styles';

//constants
import { SORT_FILTERS, SORT_BY_TEXT, SORT_ICON_SIZE } from './constants';

// types
import { Theme } from '@nykaa/ui-components/themes/types';
import { ALIGNMENT_TYPE } from 'shared/components/Filter/types';

//hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useOutsideClickHandler } from 'shared/hooks/useOutsideClickHandler';

// helpers
import { onSortApplied } from 'shared/layouts/Listing/components/Analytics/helpers';

//defs
interface SortViewProps {
  selectedSort: {
    label: string;
    value: string;
  };
  onApplyClick: (value: string) => void;
}

function SortView({ selectedSort, onApplyClick }: SortViewProps) {
  const [showOptions, setShowOptions] = useState(false);
  const theme: Theme = useTheme();
  const sortRef = useRef<HTMLDivElement>(null);

  useOutsideClickHandler(sortRef, () => setShowOptions(false));

  const onItemSelect = useCallback(
    (item: string) => {
      if (selectedSort.value !== item) {
        onApplyClick(item);

        //analytics
        onSortApplied(item);
      }
      setShowOptions(false);
    },
    [onApplyClick, selectedSort.value]
  );

  return (
    <Container
      ref={sortRef}
      onMouseEnter={() => setShowOptions(true)}
      onMouseLeave={() => setShowOptions(false)}
      onMouseMove={() => setShowOptions(true)}
    >
      <SortButton showOptions={showOptions}>
        <Wrapper>
          <SortByIcon
            size={SORT_ICON_SIZE}
            color={showOptions ? theme.colors.secondary : theme.colors.textSecondary}
          />
          <Text showOptions={showOptions}>{SORT_BY_TEXT}</Text>
          <SelectedValue showOptions={showOptions}>{selectedSort.label}</SelectedValue>
        </Wrapper>
        <Arrow
          size={SORT_ICON_SIZE}
          color={showOptions ? theme.colors.secondary : theme.colors.primary}
          reverseArrow={showOptions}
        />
      </SortButton>
      {showOptions && (
        <PopUp>
          {SORT_FILTERS.map((sort) => (
            <FilterRadio
              key={sort.value}
              value={{ id: sort.value, name: sort.label }}
              onFilterSelection={onItemSelect}
              isSelected={selectedSort.value === sort.value}
              alignmentType={ALIGNMENT_TYPE.RIGHT}
              isTickIcon
            />
          ))}
        </PopUp>
      )}
    </Container>
  );
}

export default memo(SortView);
